import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { descargaUrl, firmarUrl } from "@/lib/publicUrls";
import { Field } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Eraser, PenLine, Truck, ChevronRight, FileText } from "lucide-react";
import QrDisplay from "@/components/QrDisplay";
import { fmtFechaHora } from "@/lib/fechas";
import { LEN } from "@/lib/limites";
import { fmtNumeroDeca } from "@/lib/textos";

function AlbaranesDelDeca({ envios, currentId, pdfHref }) {
  const lista = (envios || []).filter((e) => e.firma_token);
  if (!lista.length && !pdfHref) return null;
  const pendientes = lista.filter((e) => !e.firmado_at);
  return (
    <Card className="p-4 mt-4">
      <div className="label-tag">Albaranes de este DeCA</div>
      <p className="text-xs text-slate-600 mt-1">
        Firmar uno no cierra el documento. Puedes abrir el PDF otra vez y firmar los que falten.
      </p>
      {lista.length > 0 ? (
        <div className="mt-3 space-y-2">
          {lista.map((e) => (
            e.firmado_at ? (
              <div key={e.id} className="flex items-center justify-between gap-2 border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Albarán {e.albaran_etiqueta}</div>
                  <div className="text-xs text-emerald-800 truncate">Firmado por {e.firmado_por}</div>
                </div>
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0"/>
              </div>
            ) : e.id === currentId ? (
              <div key={e.id} className="flex items-center justify-between gap-2 border border-blue-200 bg-blue-50 rounded-md px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Albarán {e.albaran_etiqueta}</div>
                  <div className="text-xs text-blue-800 truncate">Este albarán · pendiente de firma</div>
                </div>
              </div>
            ) : (
              <a key={e.id} href={`/firmar/${e.firma_token}`} className="block">
                <div className="flex items-center justify-between gap-2 border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">Albarán {e.albaran_etiqueta}</div>
                    <div className="text-xs text-slate-500 truncate">{e.destinatario_nombre} · {e.destino}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 shrink-0"/>
                </div>
              </a>
            )
          ))}
        </div>
      ) : null}
      {pendientes.filter((e) => e.id !== currentId).length > 0 ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {pendientes.filter((e) => e.id !== currentId).map((e) => (
            <div key={`qr-${e.id}`} className="border border-slate-200 rounded-md p-3 text-center">
              <div className="text-xs font-semibold text-slate-800">QR para firmar {e.albaran_etiqueta}</div>
              <div className="flex justify-center mt-2">
                <QrDisplay value={firmarUrl(e.firma_token)} size={160}/>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {pdfHref ? (
        <a href={pdfHref} target="_blank" rel="noreferrer" className="block mt-3">
          <Button variant="outline" className="w-full tap-44">
            <FileText className="h-4 w-4 mr-2"/> Abrir PDF con todos los albaranes
          </Button>
        </a>
      ) : null}
    </Card>
  );
}

export default function PublicFirmaPage() {
  const { token } = useParams();
  const canvasRef = useRef();
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);
  const [nombre, setNombre] = useState("");
  const [dni, setDni] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [deca, setDeca] = useState(null);

  const reload = async () => {
    const { data } = await axios.get(`${API}/public/e/${token}`);
    setDeca(data);
    if (data.revocado) setErr("Este DeCA está anulado. No se puede firmar.");
    return data;
  };

  useEffect(() => {
    (async () => {
      try {
        await reload();
      } catch (e) { setErr(e.response?.data?.detail || "No se pudo cargar el albarán"); }
    })();
  }, [token]); // eslint-disable-line

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: (p.clientX - r.left) * (c.width / r.width), y: (p.clientY - r.top) * (c.height / r.height) };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
    const move = (e) => {
      if (!drawing.current) return;
      e.preventDefault();
      const p = pos(e);
      ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
      last.current = p; setEmpty(false);
    };
    const end = () => { drawing.current = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move); window.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); c.addEventListener("touchend", end);
    return () => {
      c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move); window.removeEventListener("mouseup", end);
      c.removeEventListener("touchstart", start); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", end);
    };
  }, [deca?.envio?.id]);

  const clear = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height); setEmpty(true);
  };

  const envio = deca?.envio;
  const lista = deca?.envios;
  const yaFirmado = Boolean(done || envio?.firmado_at);
  const pdfHref = deca?.download_token ? descargaUrl(deca.download_token) : null;

  const submit = async () => {
    if (envio?.firmado_at || done) return;
    if (!nombre.trim()) { setErr("Introduce tu nombre"); return; }
    if (empty) { setErr("Firma en el recuadro"); return; }
    setBusy(true); setErr("");
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/public/e/${token}/firmar`, {
        nombre: nombre.trim(), dni: dni.trim() || null, firma_png_base64: dataUrl,
      });
      setDone(true);
      try { await reload(); } catch { /* la firma ya está guardada */ }
    } catch (e) {
      if (e.response?.status === 409) {
        setDone(true);
        try { await reload(); } catch { /* ignore */ }
        return;
      }
      setErr(e.response?.data?.detail || "Error al enviar la firma");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-lg mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-md bg-blue-700 flex items-center justify-center"><Truck className="h-5 w-5 text-white"/></div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Recepción por albarán</div>
            <div className="text-xl font-bold" style={{ fontFamily: "Chivo" }}>DeCA Web</div>
          </div>
        </div>

        {deca?.revocado ? (
          <Card className="p-8 text-center border-red-200 bg-red-50" data-testid="firma-blocked">
            <div className="text-lg font-semibold text-red-900" style={{ fontFamily: "Chivo" }}>DeCA anulado</div>
            <div className="text-sm text-red-800 mt-1">Este DeCA está anulado por el emisor y no puede firmarse.</div>
          </Card>
        ) : yaFirmado && envio ? (
          <Card className="p-8 text-center border-emerald-200 bg-emerald-50" data-testid="firma-ya-ok">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600"/>
            <div className="mt-3 text-lg font-semibold text-emerald-900" style={{ fontFamily: "Chivo" }}>{envio?.firmado_at && !done ? "Albarán ya firmado" : "Albarán firmado"}</div>
            <div className="text-sm text-emerald-800 mt-1">
              {envio?.firmado_por
                ? `Firmado por ${envio.firmado_por}${envio.firmado_at ? ` el ${fmtFechaHora(envio.firmado_at)}` : ""}. No se puede volver a firmar este albarán.`
                : "La recepción de este albarán ha quedado registrada. No se puede volver a firmar este albarán."}
            </div>
            {envio ? (
              <div className="mt-3 text-xs text-slate-600">Albarán {envio.albaran_etiqueta} · {envio.destinatario_nombre}</div>
            ) : null}
          </Card>
        ) : lista && !envio ? (
          <Card className="p-4">
            <div className="label-tag">Varios albaranes</div>
            <h1 className="text-2xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "Chivo" }}>Elige el albarán a firmar</h1>
            <div className="text-xs text-slate-600 mt-1">DeCA <span className="font-mono">{fmtNumeroDeca(deca)}</span> · cada destinatario firma el suyo.</div>
          </Card>
        ) : (
          <Card className="p-4">
            <div className="label-tag">Firmar recepción del albarán</div>
            <h1 className="text-2xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "Chivo" }}>Confirma la entrega</h1>
            {envio ? (
              <div className="mt-2 text-sm border border-slate-200 rounded-md p-3 bg-slate-50">
                <div className="font-semibold text-slate-900">Albarán {envio.albaran_etiqueta}</div>
                <div className="text-xs text-slate-600 mt-0.5">{envio.destinatario_nombre}</div>
                <div className="text-xs text-slate-500 mt-0.5">{envio.destino}</div>
              </div>
            ) : null}
            {deca ? (
              <div className="text-xs text-slate-600 mt-2">
                DeCA <span className="font-mono">{fmtNumeroDeca(deca)}</span> · {deca.cargador?.razon_social} → {deca.transportista?.razon_social}
              </div>
            ) : null}
            <div className="mt-4 space-y-3">
              <Field label="Nombre y apellidos *">
                <Input value={nombre} maxLength={LEN.nombre} onChange={(e)=>setNombre(e.target.value)} placeholder="Nombre completo del receptor" data-testid="firma-nombre"/>
              </Field>
              <Field label="DNI/NIF (opcional)">
                <Input value={dni} maxLength={LEN.dni} onChange={(e)=>setDni(e.target.value)} placeholder="Ej. 12345678A" data-testid="firma-dni"/>
              </Field>
              <div>
                <Label htmlFor="firma-canvas" className="label-tag flex items-center gap-1"><PenLine className="h-3 w-3" aria-hidden="true"/> Firma con el dedo</Label>
                <div className="mt-1 border-2 border-dashed border-slate-300 rounded-md bg-white touch-none">
                  <canvas id="firma-canvas" ref={canvasRef} width={600} height={200} className="w-full h-40 rounded-md" data-testid="firma-canvas" role="img" aria-label="Firma con el dedo"/>
                </div>
                <div className="flex justify-end mt-1"><Button type="button" variant="ghost" size="sm" onClick={clear} data-testid="firma-clear"><Eraser className="h-3.5 w-3.5 mr-1"/> Borrar</Button></div>
              </div>
              {err ? <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-2" data-testid="firma-error">{err}</div> : null}
              <Button disabled={busy || !envio} onClick={submit} className="w-full bg-blue-700 hover:bg-blue-800 tap-44" data-testid="firma-submit">
                {busy ? "Enviando…" : "Confirmar firma de este albarán"}
              </Button>
              <div className="text-[11px] text-slate-500 text-center">La firma se registra con IP y fecha para trazabilidad, y aplica solo a este albarán.</div>
            </div>
          </Card>
        )}

        {!deca?.revocado ? (
          <AlbaranesDelDeca envios={lista} currentId={envio?.id} pdfHref={pdfHref}/>
        ) : null}
      </div>
    </div>
  );
}
